import { PlaceOfServiceCode } from './benefit';
import { Id } from './id';
import { ChoiceOption } from './options';
import { CareLevel } from './payment-rule';

export type FacilityType = `${FacilityTypeEnum}`;

export enum FacilityTypeEnum {
  Facility = 'facility',
  NonFacility = 'non-facility',
  ASC = 'asc',
}

export type LocationRateType = `${LocationRateTypeEnum}`;

// FacilityTypeEnum + inpatient/outpatient
export enum LocationRateTypeEnum {
  Facility = 'facility',
  NonFacility = 'non-facility',
  ASC = 'asc',
  Outpatient = 'outpatient_hospital',
  Inpatient = 'inpatient_hospital',
}

export const prettyLocationRateTypes: { [key in LocationRateType]: string } = {
  facility: 'Professional facility',
  'non-facility': 'Professional non-facility',
  asc: 'ASC - technical',
  outpatient_hospital: 'Outpatient hospital',
  inpatient_hospital: 'Inpatient hospital',
};

export const locationRateTypeOptions: ChoiceOption[] = Object.entries(
  prettyLocationRateTypes,
).map(([rateType, prettyRateType]) => ({
  label: prettyRateType,
  value: rateType,
}));

export function locationRateTypeSimplified(
  locationRateType: LocationRateType,
): FacilityType | CareLevel {
  switch (locationRateType) {
    case 'outpatient_hospital':
      return 'outpatient';
    case 'inpatient_hospital':
      return 'inpatient';
    default:
      return locationRateType;
  }
}

export function locationRateTypeToFacilityType(
  locationRateType: LocationRateType,
): FacilityType {
  switch (locationRateType) {
    case 'facility':
      return 'facility';
    case 'non-facility':
      return 'non-facility';
    case 'outpatient_hospital':
    case 'inpatient_hospital':
    case 'asc':
      return 'asc';
  }
}

export function isFacilityType(r: LocationRateType): r is FacilityType {
  return r != 'outpatient_hospital' && r != 'inpatient_hospital';
}
export const FacilityTypeValues = ['facility', 'non-facility', 'asc'];

export type BusinessUnitType = `${BusinessUnitTypeEnum}`;
export type RateType = `${BusinessUnitTypeEnum}`;

export enum BusinessUnitTypeEnum {
  Professional = 'professional',
  ASC = 'asc',
  Hospital = 'hospital',
}

export function businessUnitTypeToFacilityType(
  businessUnitType: BusinessUnitType,
): FacilityType[] {
  switch (businessUnitType) {
    case 'professional':
      return [FacilityTypeEnum.Facility, FacilityTypeEnum.NonFacility];
    case 'asc':
    case 'hospital':
      return [FacilityTypeEnum.ASC];
  }
}

export function locationRateTypeToBusinessUnitType(
  locationRateType: LocationRateType,
): BusinessUnitType {
  switch (locationRateType) {
    case 'facility':
    case 'non-facility':
      return 'professional';
    case 'outpatient_hospital':
    case 'inpatient_hospital':
      return 'hospital';
    case 'asc':
      return 'asc';
  }
}

export function businessUnitTypeMatchesFacilityType(
  businessUnitType: BusinessUnitType,
  facilityType: FacilityType,
): boolean {
  switch (businessUnitType) {
    case 'professional':
      return facilityType !== 'asc';
    case 'asc':
    case 'hospital':
      return facilityType === 'asc';
  }
}

export type RateSort =
  | 'effectiveTo'
  | 'businessUnit'
  | 'payer'
  | 'facility'
  | 'schedule'
  | 'effectiveFrom';

export function getRateType(
  facilityType: FacilityType,
  billType: string | null,
): LocationRateType {
  return billType && ['12', '13', '14'].includes(billType.slice(1, 3))
    ? 'outpatient_hospital'
    : billType && billType.slice(1, 3) == '11'
      ? 'inpatient_hospital'
      : facilityType;
}

export type Location = {
  name: string;
  phone: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zip: string;
  businessUnitId: number;
  placeOfServiceCode: PlaceOfServiceCode | null;
  brandId: number | null;
  facilityType: LocationRateType | null;
  ref: string | null;
};

export type SavedLocation = Location & Id;
export type LocationBusinesssUnitFacility = SavedLocation & {
  businessUnitType: BusinessUnitType;
};

export type AutoEstimateLocation = {
  id: number;
  brandId: number | null;
  buFacilityType: BusinessUnitType;
  locationFacilityType: LocationRateType;
  placeOfServiceCode: PlaceOfServiceCode | null;
};

export type LocalityOrCbsa =
  | {
      businessUnitId: number;
      localityId: number;
      localityAreaId: number;
      censusCbsaId: null;
    }
  | {
      businessUnitId: number;
      localityId: null;
      localityAreaId: null;
      censusCbsaId: number;
    };
